import { Injectable } from '@angular/core';
import { createStore, select, setProp, withProps } from '@ngneat/elf';
import {
  addEntities,
  deleteEntities,
  selectAllEntities,
  setEntities,
  updateEntities,
  withEntities,
} from '@ngneat/elf-entities';
import { persistState } from '@ngneat/elf-persist-state';
import * as localforage from 'localforage';
import * as moment from 'moment';
import { BehaviorSubject, Observable } from 'rxjs';
import { debounceTime, tap, map } from 'rxjs/operators';
import { REFRESH_TIME_IN_MINUTES } from 'src/app/shared/configs/global-values.constants';
import { ApiBaseService } from 'src/app/shared/services/api/api-base/api-base.service';
import {
  IDeleteParams,
  IStoreProps,
  IListParams,
  IPaginationState,
  IScrollPosition,
} from 'src/app/talent-litmus-core/common/common.interface';
import { PageEvent } from '@angular/material/paginator';
import { IStickCricketGame } from './stick-cricket-game.interface';

// eslint-disable-next-line @typescript-eslint/naming-convention
const STORE_NAME = 'stickCricket-games';

const instance = localforage.createInstance({
  driver: localforage.INDEXEDDB,
  name: 'talent-litmus-adminv2',
  version: 1.0,
  storeName: STORE_NAME,
});

const store = createStore(
  { name: STORE_NAME },
  withProps<IStoreProps>({ lastRefreshed: undefined }),
  withProps<IPaginationState>({
    length: 0,
    pageIndex: 0,
    pageSize: 0,
    previousPageIndex: 0,
  }),
  withEntities<IStickCricketGame, '_id'>({ idKey: '_id' }),
  withProps<IScrollPosition>({ x: 0, y: 0 }),
  withProps<{ selectedId: string }>({ selectedId: null }),
  withProps<{ searchParam: string }>({ searchParam: '' }),
);

export const persist = persistState(store, {
  key: STORE_NAME,
  storage: instance,
});

@Injectable()
export class StickCricketGameCoreService {
  private _loading$ = new BehaviorSubject<boolean>(false);

  /**
   * @name stickCricketGameList$
   * @description emits stored list of the Stick cricket Game list
   */
  stickCricketGameList$ = store.pipe(selectAllEntities());
  paginationState$ = store.pipe(
    select((state) => {
      return state;
    }),
  );
  totalCount: number;

  public get loading$(): Observable<boolean> {
    return this._loading$.asObservable();
  }

  constructor(private _api: ApiBaseService) {
    if (!store.value.lastRefreshed) {
      setTimeout(() => {
        this.refresh();
      }, 400);
    } else {
      this.refresh();
    }
  }

  moveToLastPage(): void {
    let length: number;
    let pageSize: number;
    let previousPageIndex: number;
    this.paginationState$.subscribe((state) => {
      length = state.length;
      pageSize = state.pageSize;
      previousPageIndex = state.previousPageIndex;
    });
    this.pageChangeEvent({
      length,
      pageIndex: Math.ceil(length / pageSize),
      pageSize,
      previousPageIndex,
    });
  }

  pageChangeEvent(event: PageEvent): void {
    store.update((state) => ({
      ...state,
      length: event.length,
      pageIndex: event.pageIndex,
      pageSize: event.pageSize,
      previousPageIndex: event.previousPageIndex,
    }));
  }

  setScrollPosition(scrollPostion: [number, number]): void {
    store.update((state) => ({
      ...state,
      x: scrollPostion[0],
      y: scrollPostion[1],
    }));
  }

  setSelectedId(id: string): void {
    store.update((state) => ({
      ...state,
      selectedId: id,
    }));
  }

  setSearchParam(searchParam: string): void {
    store.update((state) => ({
      ...state,
      searchParam: searchParam,
    }));
  }

  public getStickCricketGameList(params?: IListParams): void {
    this._loading$.next(true);
    this._api
      .get(`stick-cricket-game/list?limit=1000&skip=0`, params || {})
      .pipe(debounceTime(10000))
      .subscribe((data: IstickCricketGameListResponse) => {
        this.totalCount = data.totalCount;
        store.update(
          setEntities(data.list),
          setProp('lastRefreshed', new Date()),
        );
        this._loading$.next(false);
      });
  }

  public getStickCricketGame(
    stickCricketGameId: string,
  ): Observable<IStickCricketGame> {
    return this._api.get(`stick-cricket-game/${stickCricketGameId}`);
  }

  public addStickCricketGame(
    stickCricketGame: IStickCricketGame,
  ): Observable<IStickCricketGame> {
    console.log('stickCricketGame: ', stickCricketGame);
    return this._api
      .post(`stick-cricket-game`, stickCricketGame)
      .pipe(
        tap((data: IStickCricketGame) => store.update(addEntities([data]))),
      );
  }

  public updateStickCricketGame(
    stickCricketGame: IStickCricketGame,
  ): Observable<IStickCricketGame> {
    return this._api.put(`stick-cricket-game`, stickCricketGame).pipe(
      map((data: ArrayBuffer) => data as any),
      tap((data: IStickCricketGame) =>
        store.update(
          updateEntities(data._id, (entity) => ({
            ...entity,
            name: data.name,
          })),
        ),
      ),
    );
  }

  public deleteStickCricketGame(data: IDeleteParams): Observable<any> {
    return this._api
      .delete('stick-cricket-game', { body: data })
      .pipe(tap(() => store.update(deleteEntities(data['_id']))));
  }

  public refresh(forced?: boolean): void {
    if (
      forced ||
      !store.value.lastRefreshed ||
      moment().diff(moment(store.value.lastRefreshed), 'minutes') >
        REFRESH_TIME_IN_MINUTES
    ) {
      this.getStickCricketGameList();
    }
  }
}

interface IstickCricketGameListResponse {
  list: IStickCricketGame[];
  totalCount: number;
}
